import React, { Component } from 'react';
import { Switch, Route, Link, withRouter, Redirect } from 'react-router-dom';
import moment from 'moment';
import API from '../../utils/API';
import VenueSearchModal from '../../components/sourced-venues/venueSearchModal';
import VenueDetail from './venueDetail';
import LoadingWizard from '../../components/spinner/wizard';
import { PIPELINE } from './components/VenuePipelineStrip';

const fmt = (d) => d ? moment(d).format('MMM D') : '—';
const money = (n) => (n && n > 0) ? `$${Number(n).toLocaleString()}` : '—';

const getStatusDef = (status) => PIPELINE.find(p => p.key === status) || PIPELINE[0];

/**
 * Venues — primary work surface for a proposal request.
 * Matches the wizard aesthetic: white rounded card inside the layout.
 * - Header with Add Venues CTA
 * - Status filter chips
 * - Venue list (one row per venue, all key metrics inline)
 * - Clicking a venue opens a slide-over detail with the full editor
 */
class Venues extends Component {
	constructor(props) {
		super(props);
		this.state = {
			proposalVenues: [],
			loading: true,
			searchModalOpen: false,
			activeStatus: null
		};
	}

	async componentDidMount() {
		await this.loadVenues();
		this.syncFilterFromUrl();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.location?.search !== this.props.location?.search) {
			this.syncFilterFromUrl();
		}
	}

	syncFilterFromUrl = () => {
		const params = new URLSearchParams(this.props.location.search);
		const status = params.get('status') || null;
		if (status !== this.state.activeStatus) {
			this.setState({ activeStatus: status });
		}
	};

	loadVenues = async () => {
		const { orgId, requestId } = this.props;
		this.setState({ loading: true });
		try {
			const res = await API().get(`Organizations/${orgId}/proposal-requests/${requestId}/venues`);
			this.setState({ proposalVenues: res.data || [], loading: false });
		} catch (e) {
			console.error('Error loading venues:', e);
			this.setState({ loading: false });
		}
	};

	toggleSearchModal = () => {
		this.setState({ searchModalOpen: !this.state.searchModalOpen });
	};

	handleVenuesAdded = async () => {
		await this.loadVenues();
		this.props.refreshSectionCounts && this.props.refreshSectionCounts();
		this.setState({ searchModalOpen: false });
	};

	updateVenue = (venueId, updatedData) => {
		this.setState({
			proposalVenues: this.state.proposalVenues.map(v =>
				v._id === venueId ? { ...v, ...updatedData } : v
			)
		});
	};

	removeVenue = async (proposalVenueId) => {
		const { orgId, history, requestId } = this.props;
		try {
			await API().delete(`Organizations/${orgId}/proposal-venues/${proposalVenueId}`);
			this.setState({
				proposalVenues: this.state.proposalVenues.filter(v => v._id !== proposalVenueId)
			});
			this.props.refreshSectionCounts && this.props.refreshSectionCounts();
			history.push(`/${orgId}/proposal-requests/${requestId}/venues`);
		} catch (e) {
			console.error('Error removing venue:', e);
		}
	};

	setActiveStatus = (key) => {
		const { history, orgId, requestId } = this.props;
		const next = key === this.state.activeStatus ? null : key;
		history.push(
			`/${orgId}/proposal-requests/${requestId}/venues${next ? `?status=${next}` : ''}`
		);
	};

	render() {
		const { orgId, requestId } = this.props;
		const { proposalVenues, loading, searchModalOpen, activeStatus } = this.state;

		// counts per status
		const counts = PIPELINE.reduce((acc, p) => {
			acc[p.key] = proposalVenues.filter(v => (v.status || 'Draft') === p.key).length;
			return acc;
		}, {});

		const visible = activeStatus
			? proposalVenues.filter(v => (v.status || 'Draft') === activeStatus)
			: proposalVenues;

		return (
			<>
				<div className="pr-content-wrap">
					<div className="pr-content">
						<div className="pr-content-inner" style={{ maxWidth: 'none' }}>

							{/* Header */}
							<div className="pr-venues-header">
								<div className="pr-venues-title-block">
									<h1 className="pr-section-title">Venues</h1>
									<p className="pr-section-sub">
										{proposalVenues.length === 0
											? 'Add venues that match this request, then track each proposal as it comes in.'
											: `Tracking ${proposalVenues.length} ${proposalVenues.length === 1 ? 'venue' : 'venues'}.`
										}
									</p>
								</div>
								<button
									type="button"
									className="pr-btn-primary"
									onClick={this.toggleSearchModal}
								>
									<i className="las la-plus" />
									Add Venues
								</button>
							</div>

							{/* Empty state */}
							{!loading && proposalVenues.length === 0 && (
								<div className="pr-empty">
									<i className="las la-hotel" />
									<h3>No venues added yet</h3>
									<p>Search and add venues that match your event’s location, size, and dates.</p>
									<button
										type="button"
										className="pr-btn-primary"
										onClick={this.toggleSearchModal}
									>
										<i className="las la-search-location" />
										Find Venues
									</button>
								</div>
							)}

							{/* Status filter chips */}
							{proposalVenues.length > 0 && (
								<div className="pr-status-filter">
									<button
										type="button"
										className={`pr-status-chip ${!activeStatus ? 'active' : ''}`}
										onClick={() => this.setActiveStatus(null)}
									>
										All
										<span className="pr-status-chip-count">({proposalVenues.length})</span>
									</button>
									{PIPELINE.filter(p => counts[p.key] > 0).map(p => (
										<button
											key={p.key}
											type="button"
											className={`pr-status-chip ${activeStatus === p.key ? 'active' : ''}`}
											onClick={() => this.setActiveStatus(p.key)}
											style={
												activeStatus === p.key
													? { background: p.color, borderColor: p.color, color: '#fff' }
													: undefined
											}
										>
											<span
												style={{
													width: 8,
													height: 8,
													borderRadius: '50%',
													background: activeStatus === p.key ? '#fff' : p.color,
													display: 'inline-block'
												}}
											/>
											{p.label}
											<span className="pr-status-chip-count">({counts[p.key]})</span>
										</button>
									))}
								</div>
							)}

							{/* Venue list */}
							{!loading && visible.length > 0 && (
								<div className="pr-venue-list">
									{visible.map(pv => {
										const venue = pv.venue || {};
										const def = getStatusDef(pv.status || 'Draft');
										const active = (this.props.match?.params?.venueId) === pv._id;

										return (
											<Link
												key={pv._id}
												to={`/${orgId}/proposal-requests/${requestId}/venues/${pv._id}`}
												className={`pr-venue-row ${active ? 'active' : ''}`}
											>
												<div
													className="pr-venue-thumb"
													style={venue.primaryImage ? { backgroundImage: `url(${venue.primaryImage})` } : {}}
												>
													{!venue.primaryImage && <i className="las la-hotel" />}
												</div>

												<div className="pr-venue-info">
													<h3 className="pr-venue-name">{venue.name || 'Unnamed Venue'}</h3>
													<div className="pr-venue-meta">
														{venue.location && (
															<span>
																<i className="las la-map-marker-alt" /> {venue.location}
															</span>
														)}
														{pv.sentDate && <span>Sent {fmt(pv.sentDate)}</span>}
														{pv.responseDate && <span>Replied {fmt(pv.responseDate)}</span>}
													</div>
												</div>

												<div className="pr-venue-nums">
													<div className="pr-venue-num">
														<span className="label">Room rate</span>
														<span className="value">{money(pv.quotedRoomRate)}</span>
													</div>
													<div className="pr-venue-num">
														<span className="label">Expiration Date</span>
														<span className="value">{fmt(pv.proposalExpirationDate)}</span>
													</div>
												</div>

												<span
													className="pr-venue-status"
													style={{ background: `${def.color}1a`, color: def.color }}
												>
													<span
														style={{
															width: 6,
															height: 6,
															borderRadius: '50%',
															background: def.color,
															display: 'inline-block'
														}}
													/>
													{def.label}
												</span>
											</Link>
										);
									})}
								</div>
							)}

							{/* Nothing matches filter */}
							{!loading && proposalVenues.length > 0 && visible.length === 0 && (
								<div className="pr-empty" style={{ padding: '40px 20px' }}>
									<p>No venues in this status.</p>
									<button
										type="button"
										className="pr-btn-ghost"
										onClick={() => this.setActiveStatus(null)}
									>
										Clear filter
									</button>
								</div>
							)}
						</div>
					</div>
				</div>

				<LoadingWizard loading={loading} text="Loading venues" />

				{searchModalOpen && (
					<VenueSearchModal
						isOpen={searchModalOpen}
						toggle={this.toggleSearchModal}
						orgId={orgId}
						proposalRequestId={requestId}
						onVenuesAdded={this.handleVenuesAdded}
						mode="proposalRequest"
					/>
				)}

				{/* Slide-over detail */}
				<Switch>
					<Route
						path="/:organization/proposal-requests/:request/venues/:venueId"
						render={(routeProps) => {
							const venueId = routeProps.match.params.venueId;
							const proposalVenue = proposalVenues.find(v => v._id === venueId);
							if (loading) return null;
							if (!proposalVenue) {
								return <Redirect to={`/${orgId}/proposal-requests/${requestId}/venues`} />;
							}
							return (
								<>
									<Link
										to={`/${orgId}/proposal-requests/${requestId}/venues`}
										className="pr-overlay-backdrop"
										aria-label="Close venue details"
									/>
									<div className="pr-overlay">
										<VenueDetail
											proposalVenue={proposalVenue}
											orgId={orgId}
											requestId={requestId}
											updateVenue={this.updateVenue}
											removeVenue={this.removeVenue}
											onClose={() => this.props.history.push(
												`/${orgId}/proposal-requests/${requestId}/venues`
											)}
										/>
									</div>
								</>
							);
						}}
					/>
				</Switch>
			</>
		);
	}
}

export default withRouter(Venues);
